var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _a, _WebAuth_idkiturl, _WebAuth_fbauth, _WebAuth_instance, _WebAuth_user, _WebAuth_listeners;
import { connectAuthEmulator, getAuth, signOut, } from "firebase/auth";
import { authStateFromToken } from "./auth-state.js";
import { urlJoin } from "../utils.js";
import { RefreshTokenError, RefreshTokenExpiredError, UserMissingRefreshTokenError, } from "../errors.js";
/**
 * [Singleton]
 *
 * Client to interface and perform operations about user identity
 * and authentication. Holds the single source of truth for who is
 * currently logged in.
 *
 * Designed to be shared across user and admin flows.
 */
export class WebAuth {
    get firebaseAuth() {
        return __classPrivateFieldGet(this, _WebAuth_fbauth, "f");
    }
    get baseUrl() {
        return __classPrivateFieldGet(this, _WebAuth_idkiturl, "f").href;
    }
    constructor(app, opts) {
        _WebAuth_idkiturl.set(this, void 0);
        _WebAuth_fbauth.set(this, void 0);
        _WebAuth_user.set(this, null);
        // External subscribers — notified on every state transition.
        _WebAuth_listeners.set(this, new Set());
        let identityToolkitUrl = opts.identityV1BaseUrl;
        if (!identityToolkitUrl || identityToolkitUrl === "") {
            throw new Error("Missing needed options `identityV1BaseUrl`");
        }
        // Normalise: strip trailing slashes, then add exactly one.
        identityToolkitUrl = identityToolkitUrl.trim().replace(/\/+$/g, "") + "/";
        __classPrivateFieldSet(this, _WebAuth_idkiturl, new URL(identityToolkitUrl), "f");
        const auth = getAuth(app);
        if (opts.env === "local") {
            const emulatorHost = opts.firebaseAuthEmulatorHost;
            if (!emulatorHost) {
                throw new Error("Missing needed options `firebaseAuthEmulatorHost` to run as local firebase");
            }
            // Only connect once – the emulator flag is set on the auth instance
            // after the first call to connectAuthEmulator.
            // @ts-ignore
            if (!auth.config.emulator) {
                connectAuthEmulator(auth, emulatorHost);
            }
        }
        __classPrivateFieldSet(this, _WebAuth_fbauth, auth, "f");
    }
    /**
     * Returns the singleton `AuthProvider`, creating it on the first call.
     */
    static create(app, opts) {
        if (__classPrivateFieldGet(_a, _a, "f", _WebAuth_instance) === undefined) {
            __classPrivateFieldSet(_a, _a, new _a(app, opts), "f", _WebAuth_instance);
        }
        return __classPrivateFieldGet(_a, _a, "f", _WebAuth_instance);
    }
    /**
     * Pushes the new state to every registered subscriber.
     * Errors thrown by individual subscribers are caught and logged so
     * that a misbehaving subscriber cannot prevent others from being
     * notified.
     */
    notify(state) {
        for (const listener of __classPrivateFieldGet(this, _WebAuth_listeners, "f")) {
            try {
                listener(state);
            }
            catch (err) {
                console.error("[AuthProvider] onAuthStateChange subscriber threw:", err);
            }
        }
    }
    /**
     * Stores the logged-in user and immediately notifies all subscribers.
     */
    saveAuthState(session) {
        __classPrivateFieldSet(this, _WebAuth_user, session, "f");
        this.notify(session);
    }
    /**
     * Clears the current auth state, signs out of Firebase, and notifies
     * all subscribers with `null`.
     *
     * Safe to call multiple times — subsequent calls while the user is
     * already `null` are no-ops; no subscribers are invoked.
     */
    removeAuthState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _WebAuth_user, "f") === null)
                return;
            // Sign out of Firebase to prevent the browser from silently
            // re-authenticating on the next page load or token refresh.
            try {
                yield signOut(__classPrivateFieldGet(this, _WebAuth_fbauth, "f"));
            }
            catch (err) {
                // Non-fatal — the local session is already cleared above.
                console.warn("[AuthProvider] Firebase sign-out failed:", err);
            }
            // Clear local state first so that any re-entrant call (e.g. triggered
            // by a subscriber) sees the user as already gone.
            __classPrivateFieldSet(this, _WebAuth_user, null, "f");
            this.notify(null);
        });
    }
    get currentUser() {
        return __classPrivateFieldGet(this, _WebAuth_user, "f");
    }
    /**
     * `true` when a user is logged in and has at least a session token
     * or a refresh token available.
     */
    get isAuthenticated() {
        return (__classPrivateFieldGet(this, _WebAuth_user, "f") !== null &&
            (__classPrivateFieldGet(this, _WebAuth_user, "f").session !== null || __classPrivateFieldGet(this, _WebAuth_user, "f").refreshToken !== null));
    }
    refreshSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _WebAuth_user, "f") === null)
                throw new UserMissingRefreshTokenError();
            const refreshTokenPath = __classPrivateFieldGet(this, _WebAuth_user, "f").refreshTokenPath;
            const refreshToken = __classPrivateFieldGet(this, _WebAuth_user, "f").refreshToken;
            if (!refreshToken || !refreshTokenPath) {
                console.warn("[ReauthenticatableClient] No refresh token available – the user must log in again.");
                throw new UserMissingRefreshTokenError();
            }
            // NOTE: you need to implement some authentication llogic here
            // const refreshPath = currentUser.session
            const res = yield fetch(urlJoin(this.baseUrl, refreshTokenPath), {
                method: "POST",
                body: JSON.stringify({ refreshToken: refreshToken.toString() }),
                headers: { "Content-Type": "application/json" },
            });
            if (res.status === 401) {
                // The refresh token itself has expired – the user must re-login.
                throw new RefreshTokenExpiredError(res.status);
            }
            if (!res.ok) {
                throw new RefreshTokenError(res.status);
            }
            const data = (yield res.json());
            const session = authStateFromToken(data.session.token);
            this.saveAuthState({
                session,
                refreshToken,
                refreshTokenPath: refreshTokenPath,
            });
        });
    }
    /**
     * Subscribe to auth-state changes (login, token refresh, logout).
     * Returns an unsubscribe function — call it to stop receiving updates.
     *
     * @example
     * const unsub = auth.onAuthStateChange((user) => { ... });
     * // later:
     * unsub();
     */
    onAuthStateChange(subscriber) {
        __classPrivateFieldGet(this, _WebAuth_listeners, "f").add(subscriber);
        return () => {
            __classPrivateFieldGet(this, _WebAuth_listeners, "f").delete(subscriber);
        };
    }
}
_a = WebAuth, _WebAuth_idkiturl = new WeakMap(), _WebAuth_fbauth = new WeakMap(), _WebAuth_user = new WeakMap(), _WebAuth_listeners = new WeakMap();
_WebAuth_instance = { value: void 0 };
